import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Receipt, Printer, Mail, Download, Pencil, AlertCircle, FileSignature, Settings2, Eye,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { hasPermission } from "../../utils/permissions";
import { can } from "../../utils/access";
import { stateLabel } from "../../utils/feeInvoice";
import {
  PageHeader, AppButton, AppModal, AppCard, AppCardContent, Callout, EmptyState, FormField, Input, cn,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import FilePreviewModal from "../Common/FilePreviewModal";
import Shimmer from "../Common/Shimmer";
import InvoicePdfView from "./invoice/InvoicePdfView";
import BillingDetailsModal from "./invoice/BillingDetailsModal";
import { downloadPdf, printPdf } from "./invoice/pdfActions";
import { STATUS_META, STATUS_TONE, formatCurrency, formatDate } from "./invoice/invoiceFormat";

// The invoice on this page IS the PDF (GET /fees/:id/invoice), drawn in the page
// by `invoice/InvoicePdfView` — so what the admin sees is exactly what downloads,
// prints and is emailed. Print and Download use the bytes on screen. What the
// invoice says beyond the payment (title, logo, GST, signatory…) is Settings →
// Invoices; `invoice` from GET /fees/:id feeds the side panel, which also holds
// what is about the payment but not printed on the invoice.

const BTN_LABEL = "tw-ml-[0.4rem]";

function PageFrame({ children }) {
  return (
    <div className="tw-m-0 tw-flex tw-w-full tw-max-w-full tw-flex-col tw-gap-6 print:tw-m-0 print:tw-max-w-none print:tw-p-0">
      {children}
    </div>
  );
}

/** The invoice beside its action rail; one column below a desktop, and on paper. */
function TwoColumns({ children }) {
  return (
    <div className="tw-grid tw-w-full tw-grid-cols-[minmax(0,1fr)_340px] tw-items-start tw-gap-6 max-[992px]:tw-grid-cols-1 print:tw-block">
      {children}
    </div>
  );
}

function SideTitle({ children }) {
  return (
    <h4 className="tw-mb-[0.85rem] tw-mt-0 tw-text-[0.72rem] tw-font-medium tw-uppercase tw-tracking-[0.08em] tw-text-content-subtle">
      {children}
    </h4>
  );
}

function InfoRow({ label, children }) {
  return (
    <li className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-[0.82rem]">
      <span className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.05em] tw-text-content-subtle">{label}</span>
      {children}
    </li>
  );
}

const SIDE_BTN = "tw-w-full tw-justify-start tw-gap-2";
const SIDE_VALUE = "tw-min-w-0 tw-text-right tw-font-semibold tw-text-content";
const SIDE_DIVIDER = "tw-my-6 tw-h-px tw-bg-line";

const FeeDetailsPage = () => {
  const { id, feeId } = useParams();
  const navigate = useNavigate();
  const realFeeId = feeId || id;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const [downloading, setDownloading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [showBilling, setShowBilling] = useState(false);

  const [pdf, setPdf] = useState({ bytes: null, error: "", updating: false });
  const [pdfVersion, setPdfVersion] = useState(0);

  const [showImage, setShowImage] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const usertype = getCurrentUser()?.usertype;
  const canEditBilling = usertype === "superadmin" || usertype === "admin";
  const canCustomise = canEditBilling && hasPermission("settings_payments");
  const canCollect = can("fees.collect");
  const canInvoice = can("fees.invoice");

  // Fetch fee details
  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiClient.get(`/fees/${realFeeId}`)
      .then(({ data: payload }) => {
        if (!alive) return;
        setData(payload);
        setEmailValue(payload?.transaction?.studentId?.email || "");
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.response?.data?.message || "Failed to load fee details.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [realFeeId]);

  // The invoice PDF — and again after anything that changes what it says.
  useEffect(() => {
    if (!realFeeId) return undefined;
    let alive = true;
    setPdf((p) => ({ ...p, error: "", updating: true }));
    apiClient.get(`/fees/${realFeeId}/invoice`, { responseType: "arraybuffer" })
      .then(({ data: buffer }) => {
        if (alive) setPdf({ bytes: new Uint8Array(buffer), error: "", updating: false });
      })
      .catch(() => {
        if (alive) setPdf((p) => ({ ...p, error: "The invoice could not be drawn.", updating: false }));
      });
    return () => { alive = false; };
  }, [realFeeId, pdfVersion]);

  const txn = data?.transaction;
  const invoice = data?.invoice || null;
  const invoiceNumber = data?.invoiceNumber;
  const student = txn?.studentId;
  const batchName =
    txn?.courseAssignmentId?.batchId?.name ||
    student?.batch?.name ||
    "";

  const status = STATUS_META[txn?.paymentStatus] || STATUS_META.success;
  const StatusIcon = status.Icon;
  const isOnline = txn?.paymentGateway === "razorpay";
  const txnRef = txn?.razorpayPaymentId || txn?.referenceNumber || "—";

  const handlePrint = useCallback(() => {
    if (pdf.bytes) printPdf(pdf.bytes);
  }, [pdf.bytes]);

  const handleDownload = useCallback(async () => {
    if (!realFeeId) return;
    if (pdf.bytes) {
      downloadPdf(pdf.bytes, `${invoiceNumber || "invoice"}.pdf`);
      return;
    }
    setDownloading(true);
    try {
      const res = await apiClient.get(`/fees/${realFeeId}/invoice`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${invoiceNumber || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setToast({
        show: true,
        message: e?.response?.data?.message || "Failed to download invoice.",
        type: "danger",
      });
    }
    setDownloading(false);
  }, [realFeeId, invoiceNumber, pdf.bytes]);

  const handleSend = useCallback(async () => {
    setSendError("");
    const email = (emailValue || "").trim();
    if (!email) { setSendError("Email is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSendError("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const { data: out } = await apiClient.post(`/fees/${realFeeId}/send-invoice`, { email });
      setShowSendModal(false);
      setToast({
        show: true,
        message: out?.message || `Invoice sent to ${email}`,
        type: "success",
      });
    } catch (e) {
      setSendError(e?.response?.data?.message || "Failed to send invoice. Please try again.");
    }
    setSending(false);
  }, [emailValue, realFeeId]);

  const handleViewProof = useCallback(async () => {
    if (!txn?.refImage) return;
    setShowImage(true);
    setImageLoading(true);
    setImageUrl(null);
    try {
      const { data: r } = await apiClient.get(
        `/files/presign-get?key=${encodeURIComponent(txn.refImage)}`
      );
      setImageUrl(r.url);
    } catch {
      setImageUrl(null);
    }
    setImageLoading(false);
  }, [txn]);

  const handleBillingSaved = useCallback((out) => {
    setData((d) => ({
      ...d,
      transaction: { ...d.transaction, billTo: out?.billTo || undefined },
      invoice: out?.invoice ?? d.invoice,
    }));
    setShowBilling(false);
    setPdfVersion((v) => v + 1);
    setToast({ show: true, message: out?.message || "Billing details saved.", type: "success" });
  }, []);

  const headerActions = useMemo(() => (
    <div className="no-print tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <AppButton variant="secondary" onClick={handlePrint} disabled={loading || !txn || !pdf.bytes}>
        <Printer size={16} />
        <span className={BTN_LABEL}>Print</span>
      </AppButton>
      <AppButton variant="secondary" onClick={handleDownload} disabled={loading || !txn || downloading}>
        {downloading ? <Spinner animation="border" size="sm" /> : <Download size={16} />}
        <span className={BTN_LABEL}>Download PDF</span>
      </AppButton>
      {canInvoice && (
        <AppButton onClick={() => { setSendError(""); setShowSendModal(true); }} disabled={loading || !txn}>
          <Mail size={16} />
          <span className={BTN_LABEL}>Send Invoice</span>
        </AppButton>
      )}
    </div>
  ), [handlePrint, handleDownload, loading, txn, downloading, pdf.bytes, canInvoice]);

  // ─── Render states ────────────────────────────────────────────────
  // A Shimmer in the shape of the page: the header strip, then the invoice
  // sheet beside its action rail, so nothing jumps when the data lands.
  if (loading) {
    return (
      <PageFrame>
        <Shimmer height={56} />
        <TwoColumns>
          <Shimmer height={620} />
          <Shimmer height={340} />
        </TwoColumns>
      </PageFrame>
    );
  }

  if (err || !txn) {
    return (
      <PageFrame>
        <div className="no-print">
          <PageHeader
            title="Fee Invoice Details"
            icon={Receipt}
            backButton
          />
        </div>
        <AppCard>
          <AppCardContent>
            <EmptyState
              icon={<AlertCircle size={40} />}
              title="Fee record not found"
              description={err || "We couldn't load this fee transaction."}
            />
          </AppCardContent>
        </AppCard>
      </PageFrame>
    );
  }

  const billTo = txn.billTo || null;
  const gst = invoice?.gst || null;

  return (
    <PageFrame>
      {/* ── Header with actions (hidden on print) ── */}
      <div className="no-print">
        <PageHeader
          title="Fee Invoice Details"
          description={`Invoice ${invoiceNumber}`}
          icon={Receipt}
          actions={headerActions}
          backButton
        />
      </div>

      <TwoColumns>
        <InvoicePdfView
          className="print-sheet"
          bytes={pdf.bytes}
          label={`Invoice ${invoiceNumber}`}
          updating={pdf.updating && Boolean(pdf.bytes)}
          error={pdf.error}
          onRetry={() => setPdfVersion((v) => v + 1)}
        />

        {/* Quick actions sidebar — hidden on print */}
        <aside className="no-print tw-sticky tw-top-20 max-[992px]:tw-static">
          <AppCard>
            <AppCardContent>
              <SideTitle>Quick Actions</SideTitle>
              <div className="tw-flex tw-flex-col tw-gap-2">
                <AppButton variant="secondary" onClick={handlePrint} disabled={!pdf.bytes} className={SIDE_BTN}>
                  <Printer size={16} /> Print Invoice
                </AppButton>
                <AppButton
                  variant="secondary"
                  onClick={handleDownload}
                  disabled={downloading}
                  className={SIDE_BTN}
                >
                  {downloading
                    ? <Spinner animation="border" size="sm" />
                    : <Download size={16} />}
                  Download PDF
                </AppButton>
                {canInvoice && (
                  <AppButton
                    onClick={() => { setSendError(""); setShowSendModal(true); }}
                    className={SIDE_BTN}
                  >
                    <Mail size={16} /> Send to Email
                  </AppButton>
                )}
                {canCollect && student?._id && txn?._id && (
                  <AppButton
                    variant="ghost"
                    onClick={() => navigate(`/students/${student._id}/fees/edit/${txn._id}`)}
                    className={SIDE_BTN}
                  >
                    <Pencil size={16} /> Edit Fee
                  </AppButton>
                )}
              </div>

              {canEditBilling && (
                <>
                  <div className={SIDE_DIVIDER} />
                  <SideTitle>Made out to</SideTitle>
                  <div className="tw-flex tw-flex-col tw-gap-1 tw-text-[0.82rem]">
                    <strong className="tw-font-semibold tw-text-content">
                      {invoice?.billTo?.name || student?.name || "—"}
                    </strong>
                    <span className="tw-text-content-muted">
                      {billTo ? "Billing details set for this invoice." : "The student, from their record."}
                    </span>
                    {billTo?.gstin && <span className="tw-text-content-muted">GSTIN {billTo.gstin}</span>}
                    {gst?.placeOfSupply && (
                      <span className="tw-text-content-muted">
                        Place of supply: {stateLabel(gst.placeOfSupply)} · {gst.split === "inter" ? "IGST" : "CGST + SGST"}
                      </span>
                    )}
                  </div>
                  {canInvoice && (
                    <AppButton variant="outline" size="sm" className="tw-mt-3" onClick={() => setShowBilling(true)}>
                      <FileSignature size={14} /> {billTo ? "Change billing details" : "Bill someone else"}
                    </AppButton>
                  )}
                </>
              )}

              {gst?.paidInclusive && (
                <Callout tone="info" className="tw-mt-4">
                  Your fees are set as before GST, but this was paid online, and the gateway collected exactly{" "}
                  {formatCurrency(txn.currentPayment)}. So the invoice treats that amount as including GST.
                </Callout>
              )}

              <div className={SIDE_DIVIDER} />

              <SideTitle>Transaction Info</SideTitle>
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-[0.55rem] tw-p-0">
                <InfoRow label="Status">
                  <span className={cn("tw-inline-flex tw-items-center tw-gap-[0.35rem] tw-rounded-full tw-border tw-border-solid tw-px-2 tw-py-[0.18rem] tw-text-[0.68rem] tw-font-semibold tw-leading-none", STATUS_TONE[status.tone])}>
                    <StatusIcon size={12} />
                    {status.label}
                  </span>
                </InfoRow>
                <InfoRow label="Gateway">
                  <strong className={SIDE_VALUE}>{isOnline ? "Razorpay" : "Manual"}</strong>
                </InfoRow>
                <InfoRow label="Mode">
                  <strong className={SIDE_VALUE}>{txn.paymentMode || "—"}</strong>
                </InfoRow>
                <InfoRow label="Reference">
                  <strong className="tw-inline-block tw-max-w-[160px] tw-truncate tw-align-middle tw-font-semibold tw-text-content">{txnRef}</strong>
                </InfoRow>
                <InfoRow label="Created">
                  <strong className={SIDE_VALUE}>{formatDate(txn.createdAt, true)}</strong>
                </InfoRow>
                {txn.paidTo && (
                  <InfoRow label="Received By">
                    <strong className={SIDE_VALUE}>{txn.paidTo}</strong>
                  </InfoRow>
                )}
                {batchName && (
                  <InfoRow label="Batch">
                    <strong className={SIDE_VALUE}>{batchName}</strong>
                  </InfoRow>
                )}
              </ul>
              {txn.refImage && (
                <AppButton variant="outline" size="sm" className="tw-mt-3" onClick={handleViewProof}>
                  <Eye size={14} /> View payment proof
                </AppButton>
              )}

              {student && (student.totalFees != null || student.pendingFees != null) && (
                <>
                  <div className={SIDE_DIVIDER} />
                  <SideTitle>Student&apos;s balance{gst?.pricing === "exclusive" ? " (before GST)" : ""}</SideTitle>
                  <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-[0.55rem] tw-p-0">
                    <InfoRow label="Course fees">
                      <strong className={SIDE_VALUE}>{formatCurrency(student.totalFees)}</strong>
                    </InfoRow>
                    <InfoRow label="Paid so far">
                      <strong className={cn(SIDE_VALUE, "tw-text-success-fg")}>{formatCurrency(student.paidFees)}</strong>
                    </InfoRow>
                    <InfoRow label="Outstanding">
                      <strong className={cn(SIDE_VALUE, Number(student.pendingFees) > 0 ? "tw-text-warning-fg" : "tw-text-success-fg")}>
                        {formatCurrency(student.pendingFees)}
                      </strong>
                    </InfoRow>
                    {txn.nextPaymentDate && (
                      <InfoRow label="Next due">
                        <strong className={SIDE_VALUE}>{formatDate(txn.nextPaymentDate)}</strong>
                      </InfoRow>
                    )}
                  </ul>
                </>
              )}

              {canCustomise && (
                <>
                  <div className={SIDE_DIVIDER} />
                  <Link
                    to="/settings?tab=invoices"
                    className="tw-inline-flex tw-items-center tw-gap-2 tw-text-[0.82rem] tw-font-medium tw-text-primary"
                  >
                    <Settings2 size={14} /> Customise your invoices
                  </Link>
                  <p className="tw-m-0 tw-mt-1 tw-text-[0.75rem] tw-text-content-subtle">
                    Logo, GST, signature and notes — for every invoice.
                  </p>
                </>
              )}
            </AppCardContent>
          </AppCard>
        </aside>
      </TwoColumns>

      {/* ── Send invoice modal ── */}
      <AppModal
        show={showSendModal}
        onHide={() => { if (!sending) setShowSendModal(false); }}
        title="Send Invoice via Email"
        icon={<Mail size={18} />}
        size="sm"
        footer={
          <>
            <AppButton
              variant="secondary"
              onClick={() => setShowSendModal(false)}
              disabled={sending}
            >
              Cancel
            </AppButton>
            <AppButton onClick={handleSend} disabled={sending}>
              {sending ? <><Spinner animation="border" size="sm" /> Sending…</> : (<><Mail size={16} /> Send Invoice</>)}
            </AppButton>
          </>
        }
      >
        <div className="tw-flex tw-flex-col tw-gap-3">
          <p className="tw-m-0 tw-text-[0.9rem] tw-leading-[1.5] tw-text-content-muted">
            The invoice PDF for{" "}
            <strong className="tw-text-content">{invoiceNumber}</strong>{" "}
            (<strong className="tw-text-content">{formatCurrency(txn.currentPayment)}</strong>) will be emailed to:
          </p>
          <FormField
            label="Recipient Email"
            required
            error={sendError || null}
            hint="Defaults to the student's registered email. You can override it."
          >
            <Input
              type="email"
              value={emailValue}
              onChange={(e) => { setEmailValue(e.target.value); setSendError(""); }}
              placeholder="student@example.com"
              disabled={sending}
              autoFocus
            />
          </FormField>
        </div>
      </AppModal>

      <BillingDetailsModal
        show={showBilling}
        onHide={() => setShowBilling(false)}
        feeId={txn._id}
        billTo={billTo}
        student={student}
        gst={gst}
        onSaved={handleBillingSaved}
      />

      <FilePreviewModal
        show={showImage}
        onHide={() => { setShowImage(false); setImageUrl(null); }}
        url={imageUrl}
        fileName="Payment Proof"
        loading={imageLoading}
        isImage
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </PageFrame>
  );
};

export default FeeDetailsPage;
